import { useState, useEffect } from 'react';
import { Calendar, ChevronRight, Syringe, Shield, Heart, Search, Thermometer, TestTube2, ClipboardList, HeartPulse, Wind, CigaretteOff, CalendarCheck, Sun, AlertTriangle, TrendingUp, Clock, Euro, Users, Target, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  syringe: Syringe,
  shield: Shield,
  heart: Heart,
  search: Search,
  thermometer: Thermometer,
  'test-tube': TestTube2,
  clipboard: ClipboardList,
  'heart-pulse': HeartPulse,
  wind: Wind,
  'cigarette-off': CigaretteOff,
  'calendar-check': CalendarCheck,
  sun: Sun,
  'alert-triangle': AlertTriangle,
  calendar: Calendar,
};

const CATEGORY_LABELS = {
  vaccination: 'Vaccination',
  depistage: 'D\u00e9pistage',
  trod: 'TROD',
  entretien: 'Entretien',
  prevention: 'Pr\u00e9vention',
  autre: 'Autre',
};

const STATUS_CONFIG = {
  active: { label: 'En cours', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  upcoming: { label: '\u00c0 venir', bg: 'bg-amber-50/50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  past: { label: 'Termin\u00e9e', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
};

function CampaignCard({ campaign }) {
  const [expanded, setExpanded] = useState(false);
  const sc = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.upcoming;
  const IconComp = ICON_MAP[campaign.icon] || Calendar;

  return (
    <div
      className={`rounded-xl border ${sc.border} ${sc.bg} p-5 transition-all hover:shadow-md`}
      data-testid={`campaign-card-${campaign.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: campaign.color + '15' }}>
            <IconComp size={20} style={{ color: campaign.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-[#1B2A4A] text-sm">{campaign.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${sc.badge}`}>
                {sc.label}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{campaign.period_label}</p>
          </div>
        </div>

        {/* Countdown or status */}
        {campaign.status === 'upcoming' && campaign.days_until > 0 && (
          <div className="text-right shrink-0">
            <div className="text-lg font-black text-amber-600">J-{campaign.days_until}</div>
            <div className="text-[10px] text-amber-500 font-medium">avant debut</div>
          </div>
        )}
        {campaign.status === 'active' && (
          <div className="shrink-0">
            <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
          </div>
        )}
      </div>

      {/* Potential */}
      {campaign.remuneration_per_act > 0 && (
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs">
            <Users size={13} className="text-gray-400" />
            <span className="text-gray-600">~{campaign.eligible_estimate.toLocaleString('fr-FR')} \u00e9ligibles</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Euro size={13} className="text-gray-400" />
            <span className="text-gray-600">{campaign.remuneration_per_act.toFixed(2)} EUR/acte</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-bold" style={{ color: campaign.color }}>
            <Target size={13} />
            <span>Potentiel : {campaign.potential_eur.toLocaleString('fr-FR')} EUR</span>
          </div>
        </div>
      )}
      {campaign.remuneration_per_act === 0 && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
          <Euro size={13} />
          <span>Pas de r&eacute;mun&eacute;ration directe — fid&eacute;lisation et image</span>
        </div>
      )}

      {/* Target */}
      <div className="mt-2 text-xs text-gray-500">
        <span className="font-semibold text-gray-600">Cible : </span>{campaign.target_summary}
      </div>

      {/* Expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#1B2A4A] hover:text-[#B8860B] transition-colors"
        data-testid={`campaign-expand-${campaign.id}`}
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {expanded ? 'Masquer les d\u00e9tails' : 'Voir les actions recommand\u00e9es'}
      </button>

      {/* Details */}
      {expanded && (
        <div className="mt-3 space-y-3 animate-in fade-in duration-200">
          <p className="text-xs text-gray-600 leading-relaxed">{campaign.description}</p>

          {campaign.actions_recommended?.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-3">
              <p className="text-[10px] font-bold text-[#1B2A4A] uppercase tracking-wider mb-2">Actions recommand&eacute;es</p>
              <div className="space-y-1.5">
                {campaign.actions_recommended.map((action, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold text-white" style={{ backgroundColor: campaign.color }}>
                      {i + 1}
                    </div>
                    <span className="leading-relaxed">{action}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {campaign.remuneration_source && (
            <p className="text-[10px] text-gray-400">
              Source : {campaign.remuneration_source}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TimelineBar({ campaigns }) {
  const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonth = new Date().getMonth();

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 overflow-x-auto" data-testid="campaigns-timeline">
      <div className="min-w-[700px]">
        {/* Month headers */}
        <div className="flex border-b border-gray-100 pb-2 mb-3">
          <div className="w-32 shrink-0" />
          {months.map((m, i) => (
            <div key={m} className={`flex-1 text-center text-[10px] font-bold uppercase tracking-wider ${i === currentMonth ? 'text-[#B8860B]' : 'text-gray-400'}`}>
              {m}
              {i === currentMonth && <div className="w-1.5 h-1.5 rounded-full bg-[#B8860B] mx-auto mt-1" />}
            </div>
          ))}
        </div>

        {/* Campaign bars */}
        {campaigns.map(c => {
          const sm = c.period_start_month || 1;
          const em = c.period_end_month || 12;

          return (
            <div key={c.id} className="flex items-center mb-1.5">
              <div className="w-32 shrink-0 text-[10px] font-semibold text-gray-600 truncate pr-2">{c.short_name}</div>
              <div className="flex flex-1 relative h-5">
                {months.map((_, i) => {
                  const monthNum = i + 1;
                  let isActive;
                  if (em >= sm) {
                    isActive = monthNum >= sm && monthNum <= em;
                  } else {
                    isActive = monthNum >= sm || monthNum <= em;
                  }
                  return (
                    <div key={i} className="flex-1 px-px">
                      <div
                        className={`h-full rounded-sm transition-all ${isActive ? 'opacity-80' : 'bg-gray-50'}`}
                        style={isActive ? { backgroundColor: c.color } : {}}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('cards');

  useEffect(() => {
    const token = localStorage.getItem('benchory_token');
    fetch(`${API_URL}/campaigns`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setCampaigns(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" />
      </div>
    );
  }

  const filtered = filter === 'all' ? campaigns : campaigns.filter(c => c.status === filter);

  // Stats
  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  const upcomingCampaigns = campaigns.filter(c => c.status === 'upcoming');
  const totalPotential = campaigns.filter(c => c.remuneration_per_act > 0 && c.status !== 'past').reduce((s, c) => s + c.potential_eur, 0);
  const nextCampaign = upcomingCampaigns[0];

  return (
    <div className="space-y-6" data-testid="campaigns-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <TrendingUp size={16} className="text-emerald-600" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">En cours</span>
          </div>
          <p className="text-2xl font-black text-[#1B2A4A]">{activeCampaigns.length}</p>
          <p className="text-xs text-gray-400">campagne{activeCampaigns.length > 1 ? 's' : ''} active{activeCampaigns.length > 1 ? 's' : ''}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Clock size={16} className="text-amber-600" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">A venir</span>
          </div>
          <p className="text-2xl font-black text-[#1B2A4A]">{upcomingCampaigns.length}</p>
          {nextCampaign && <p className="text-xs text-amber-600 font-medium">Prochaine : {nextCampaign.short_name} (J-{nextCampaign.days_until})</p>}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#B8860B]/10 flex items-center justify-center">
              <Euro size={16} className="text-[#B8860B]" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Potentiel annuel</span>
          </div>
          <p className="text-2xl font-black text-[#B8860B]">{totalPotential.toLocaleString('fr-FR')} EUR</p>
          <p className="text-xs text-gray-400">sur les campagnes r&eacute;mun&eacute;r&eacute;es</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Calendar size={16} className="text-blue-600" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total</span>
          </div>
          <p className="text-2xl font-black text-[#1B2A4A]">{campaigns.length}</p>
          <p className="text-xs text-gray-400">campagnes dans le r&eacute;f&eacute;rentiel</p>
        </div>
      </div>

      {/* Timeline view */}
      <TimelineBar campaigns={campaigns} />

      {/* Filters + view toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {[
            { key: 'all', label: 'Toutes' },
            { key: 'active', label: 'En cours' },
            { key: 'upcoming', label: '\u00c0 venir' },
            { key: 'past', label: 'Termin\u00e9es' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filter === f.key ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">{filtered.length} campagne{filtered.length > 1 ? 's' : ''}</p>
      </div>

      {/* Campaign cards */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <Calendar size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucune campagne dans cette cat&eacute;gorie</p>
          </div>
        ) : (
          filtered.map(c => <CampaignCard key={c.id} campaign={c} />)
        )}
      </div>
    </div>
  );
}
